/**
 * Tier Window — Tier range construction and question lookup
 *
 * Functions for building difficulty tier windows and finding
 * candidate questions within those windows.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { type QuestionCore, QUESTIONS_BY_FAMILY } from '../../content'
import { isPlusTier } from '../../governance'
import { V11_CONFIG, debugLog } from './selectionConfig'

/**
 * Build a set of difficulty tiers from a tier range,
 * INTERSECTED with allowed tiers for the group.
 *
 * v11.3: Plus-tier gating — excludes plus tiers from the window
 * when the student's mastery is below PLUS_UNLOCK_MASTERY.
 * This prevents struggling students from receiving transfer/analysis
 * questions before they've consolidated the base procedure.
 *
 * @param centerTier - The target tier to build the window around
 * @param below - How many tiers below center to include
 * @param above - How many tiers above center to include
 * @param allowedTiers - Set of tiers allowed for the current group
 * @param currentMastery - Optional: current mastery score for plus-tier gating
 */
export function buildTierWindow(
  centerTier: number,
  below: number,
  above: number,
  allowedTiers: Set<number>,
  currentMastery?: number,
): Set<number> {
  const window = new Set<number>()
  const minTier = centerTier - below
  const maxTier = centerTier + above
  for (let tier = minTier; tier <= maxTier; tier++) {
    // Only include tiers that are in the allowed set
    if (!allowedTiers.has(tier)) continue

    // v11.3: Plus-tier gating
    // If the student hasn't reached PLUS_UNLOCK_MASTERY, exclude plus tiers
    // UNLESS the center tier itself is a plus tier (student is already working at plus level)
    if (
      isPlusTier(tier) &&
      !isPlusTier(centerTier) &&
      currentMastery !== undefined &&
      currentMastery < V11_CONFIG.PLUS_UNLOCK_MASTERY
    ) {
      debugLog(
        `   🔒 Plus-tier ${tier} excluded from window (mastery ${currentMastery} < ${V11_CONFIG.PLUS_UNLOCK_MASTERY} threshold)`,
      )
      continue
    }

    window.add(tier)
  }
  return window
}

/**
 * Find candidate questions for a specific familyId within a tier window.
 * Uses the QUESTIONS_BY_FAMILY index for O(1) lookup.
 */
export function findQuestionsForFamily(
  familyId: string,
  tierWindow: Set<number>,
  excludeQuestionIds: Set<string>,
): QuestionCore[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId) || []
  return familyQuestions.filter((q) => {
    // Must be within tier window
    if (!tierWindow.has(q.tier)) return false
    // Must not already be in session
    if (excludeQuestionIds.has(q.id)) return false
    return true
  })
}
